// JobClose AI · Account Deletion Safety Bundle A · Frontend UX
//
// Renders the "Danger zone" panel inside Settings/Account. Consumes the
// Bundle A backend endpoints ONLY (no cascade destruction, no Bundle B
// behaviour):
//
//   POST /api/auth/reauthenticate           { password }
//   POST /api/auth/request-account-deletion
//   POST /api/organizations/{id}/request-deletion { confirmation }
//   POST /api/organizations/{id}/cancel-deletion
//   POST /api/memberships/{id}/promote-to-owner
//   POST /api/auth/cancel-account-deletion-by-email { email, password }
//
// UX invariants (per PO):
//   * Reauth is required on EACH destructive click (no session unlock).
//   * Multi-user Owner must first transfer ownership; UI enforces the
//     same rule the backend enforces, and never presents self-delete
//     while other active members exist and the user is the sole Owner.
//   * Sole Owner sees "Delete Company" with typed DELETE COMPANY
//     confirmation, which triggers Stripe cancel_at_period_end on the
//     backend (no direct Stripe call from the client).
//   * Success path clears session and bounces to /login.

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";

const humanize = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") {
    const map = {
      invalid_password: "That password is incorrect.",
      reauthentication_required: "Please confirm your password again.",
      confirmation_required: "Type DELETE COMPANY to confirm.",
      account_deleted: "This account is already scheduled for deletion.",
      already_deleted: "This account is already scheduled for deletion.",
      cross_tenant_forbidden: "You can only delete your own company.",
      only_requester_can_cancel:
        "Only the person who requested deletion can cancel it.",
      not_pending_deletion: "There is no pending deletion to cancel.",
      no_active_organization: "Your company workspace is not fully set up yet.",
      owner_required: "Only the company owner can perform this action.",
      cannot_promote_self: "You can't promote yourself.",
      already_owner: "That teammate is already an owner.",
      membership_not_found: "That teammate was not found on your team.",
    };
    if (map[detail]) return map[detail];
    if (typeof detail === "string") return detail;
  }
  if (err?.response?.data?.detail?.error) {
    return err.response.data.detail.error.replace(/_/g, " ");
  }
  return fallback || "Something went wrong. Please try again.";
};

const clearSessionAndBounce = () => {
  try {
    localStorage.removeItem("jc_token");
    localStorage.removeItem("jc_user");
    localStorage.removeItem("jc.levi.session_id");
    sessionStorage.removeItem("jc:levi:fresh_login");
    sessionStorage.removeItem("jc:levi:manual_mode");
  } catch {
    /* ignore */
  }
  window.location.href = "/login";
};

const AccountDangerZone = () => {
  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState([]);
  const [orgStatus, setOrgStatus] = useState(null); // "active"|"pending_deletion"|null

  // Modal state ────────────────────────────────────────────────────────
  // action ∈ null | "delete-account" | "delete-company" | "transfer"
  const [action, setAction] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [transferTargetId, setTransferTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [postDelete, setPostDelete] = useState(null); // { kind, graceEndsAt, orgId? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mems, seatsResp] = await Promise.all([
        api.get("/memberships").catch(() => ({ data: [] })),
        api.get("/organizations/seats").catch(() => ({ data: null })),
      ]);
      setMemberships(Array.isArray(mems.data) ? mems.data : []);
      // We don't have a dedicated org-status GET; if the caller can
      // reach /organizations/seats, the org is not in pending_deletion
      // (the auth guard would have 403'd). Anything else we treat as
      // "active" for UI purposes.
      setOrgStatus(seatsResp?.data ? "active" : null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Derived role/scope information ─────────────────────────────────────
  const scope = useMemo(() => {
    const uid = user?.id;
    const active = memberships.filter((m) => m.status === "active");
    const mine = active.find((m) => m.user_id === uid);
    const iAmOwner = mine?.role === "owner";
    const otherActive = active.filter((m) => m.user_id !== uid);
    const otherOwners = otherActive.filter((m) => m.role === "owner");
    const otherMembers = otherActive; // any other active person
    // Eligible transfer targets: any other active member (not just non-owners).
    const eligibleTargets = otherActive;
    let kind = "member";
    if (iAmOwner) {
      if (otherMembers.length === 0) kind = "owner_sole";
      else if (otherOwners.length > 0) kind = "owner_with_co_owner";
      else kind = "owner_with_members";
    }
    return {
      kind, // "member" | "owner_sole" | "owner_with_members" | "owner_with_co_owner"
      iAmOwner,
      eligibleTargets,
      mineMembershipId: mine?.id || null,
    };
  }, [memberships, user]);

  const resetModal = () => {
    setAction(null);
    setPassword("");
    setConfirmationText("");
    setTransferTargetId("");
    setBusy(false);
  };

  // Destructive submit handlers ────────────────────────────────────────
  const submitDeleteAccount = async () => {
    if (!password) {
      toast.error("Enter your password to confirm.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reauthenticate", { password });
      const { data } = await api.post("/auth/request-account-deletion");
      setPostDelete({
        kind: "account",
        graceEndsAt: data?.deletion_grace_ends_at || null,
      });
      resetModal();
    } catch (err) {
      toast.error(humanize(err, "Could not delete your account."));
      setBusy(false);
    }
  };

  const submitTransfer = async () => {
    if (!transferTargetId) {
      toast.error("Choose a teammate to receive ownership.");
      return;
    }
    if (!password) {
      toast.error("Enter your password to confirm.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reauthenticate", { password });
      await api.post(`/memberships/${transferTargetId}/promote-to-owner`);
      toast.success("Ownership shared. You can now delete your account.");
      resetModal();
      await load();
    } catch (err) {
      toast.error(humanize(err, "Could not transfer ownership."));
      setBusy(false);
    }
  };

  const submitDeleteCompany = async () => {
    if (confirmationText.trim() !== "DELETE COMPANY") {
      toast.error("Type DELETE COMPANY exactly to confirm.");
      return;
    }
    if (!password) {
      toast.error("Enter your password to confirm.");
      return;
    }
    const orgId = user?.organization_id;
    if (!orgId) {
      toast.error("Your company workspace is not fully set up yet.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reauthenticate", { password });
      const { data } = await api.post(
        `/organizations/${orgId}/request-deletion`,
        { confirmation: "DELETE COMPANY" },
      );
      setPostDelete({
        kind: "company",
        graceEndsAt: data?.deletion_grace_ends_at || null,
        orgId,
      });
      resetModal();
    } catch (err) {
      toast.error(humanize(err, "Could not delete your company."));
      setBusy(false);
    }
  };

  // Cancel org deletion — still uses the requester's JWT.
  const cancelCompanyDeletion = async () => {
    if (!postDelete?.orgId) return;
    setBusy(true);
    try {
      await api.post(`/organizations/${postDelete.orgId}/cancel-deletion`);
      toast.success(
        "Company deletion canceled. Your workspace is fully restored.",
      );
      setPostDelete(null);
      // Reload page so the auth guard revalidates.
      window.location.reload();
    } catch (err) {
      toast.error(humanize(err, "Could not cancel deletion."));
      setBusy(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // Render — post-delete confirmation screen
  // ────────────────────────────────────────────────────────────────────
  if (postDelete) {
    const graceStr = postDelete.graceEndsAt
      ? new Date(postDelete.graceEndsAt).toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "in 30 days";
    return (
      <div
        className="jc-card p-6 border-l-4 border-l-[var(--jc-warn,#b45309)]"
        data-testid="deletion-scheduled-panel"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck
            size={22}
            className="text-[var(--jc-warn,#b45309)] shrink-0 mt-0.5"
          />
          <div className="min-w-0">
            <div className="overline">
              {postDelete.kind === "company"
                ? "Company deletion scheduled"
                : "Account deletion scheduled"}
            </div>
            <h3 className="font-display text-xl mt-1">
              Grace period until {graceStr}
            </h3>
            <p className="text-sm text-[var(--jc-text-soft)] mt-2 leading-relaxed">
              {postDelete.kind === "company" ? (
                <>
                  Your company has been placed in a 30-day grace period.
                  Billing has been set to end at the current period. Anyone on
                  your team is signed out immediately. Nothing is permanently
                  destroyed during the grace window.
                </>
              ) : (
                <>
                  Your personal account has been placed in a 30-day grace
                  period. You will be signed out immediately. Company data is
                  preserved for your team. You can restore your account at any
                  time during the grace window from the sign-in screen.
                </>
              )}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {postDelete.kind === "company" && (
                <button
                  type="button"
                  onClick={cancelCompanyDeletion}
                  disabled={busy}
                  data-testid="cancel-company-deletion-btn"
                  className="px-3 py-2 rounded-md text-xs font-semibold jc-btn-primary disabled:opacity-60 flex items-center gap-1.5"
                >
                  {busy ? (
                    <Loader2 className="animate-spin" size={13} />
                  ) : null}
                  Never mind — restore my company
                </button>
              )}
              <button
                type="button"
                onClick={clearSessionAndBounce}
                data-testid="post-deletion-logout-btn"
                className="px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // Render — default danger-zone panel
  // ────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="jc-card p-6" data-testid="danger-zone-loading">
        <div className="flex items-center gap-2 text-sm text-[var(--jc-text-soft)]">
          <Loader2 className="animate-spin" size={14} /> Loading account
          settings…
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="jc-card p-6 border border-[var(--jc-warn,#b45309)]/40"
        data-testid="danger-zone-card"
      >
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle
            size={20}
            className="text-[var(--jc-warn,#b45309)] shrink-0 mt-0.5"
          />
          <div>
            <div className="overline text-[var(--jc-warn,#b45309)]">
              Danger zone
            </div>
            <h3 className="font-display text-xl mt-1">
              Delete account or company
            </h3>
            <p className="text-sm text-[var(--jc-text-soft)] mt-1">
              Every destructive action requires your password again. Nothing is
              destroyed immediately — a 30-day grace period lets you restore
              your workspace if you change your mind.
            </p>
          </div>
        </div>

        {/* Member — simple self-delete */}
        {scope.kind === "member" && (
          <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg bg-[var(--jc-bg)] border border-[var(--jc-border)]">
            <div className="min-w-0">
              <div className="font-semibold text-sm">Delete my account</div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-1">
                Removes your personal login. Your company's data stays with the
                owner.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAction("delete-account")}
              data-testid="open-delete-account-btn"
              className="shrink-0 px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-warn,#b45309)] text-[var(--jc-warn,#b45309)] hover:bg-[var(--jc-warn,#b45309)]/10"
            >
              Delete my account
            </button>
          </div>
        )}

        {/* Owner with co-owner — safe to self-delete */}
        {scope.kind === "owner_with_co_owner" && (
          <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg bg-[var(--jc-bg)] border border-[var(--jc-border)]">
            <div className="min-w-0">
              <div className="font-semibold text-sm">Delete my account</div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-1">
                Another owner will keep running your company. Your login is
                removed.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAction("delete-account")}
              data-testid="open-delete-account-btn"
              className="shrink-0 px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-warn,#b45309)] text-[var(--jc-warn,#b45309)] hover:bg-[var(--jc-warn,#b45309)]/10"
            >
              Delete my account
            </button>
          </div>
        )}

        {/* Owner with members but no co-owner — must transfer first */}
        {scope.kind === "owner_with_members" && (
          <div
            className="mt-2 p-4 rounded-lg bg-[var(--jc-bg)] border border-[var(--jc-border)]"
            data-testid="owner-must-transfer-block"
          >
            <div className="font-semibold text-sm">Transfer ownership</div>
            <p className="text-xs text-[var(--jc-text-soft)] mt-1">
              You're the only owner of your company. Share ownership with a
              teammate first — then you can delete your account.
            </p>
            <button
              type="button"
              onClick={() => setAction("transfer")}
              disabled={scope.eligibleTargets.length === 0}
              data-testid="open-transfer-ownership-btn"
              className="mt-3 px-3 py-2 rounded-md text-xs font-semibold jc-btn-primary disabled:opacity-60"
            >
              Share ownership with a teammate
            </button>
            <div className="mt-4 pt-4 border-t border-[var(--jc-border)]">
              <div className="text-xs text-[var(--jc-text-soft)]">
                Prefer to close the whole workspace instead?
              </div>
              <button
                type="button"
                onClick={() => setAction("delete-company")}
                data-testid="open-delete-company-btn"
                className="mt-2 px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-warn,#b45309)] text-[var(--jc-warn,#b45309)] hover:bg-[var(--jc-warn,#b45309)]/10"
              >
                Delete the entire company
              </button>
            </div>
          </div>
        )}

        {/* Sole owner — must use company deletion */}
        {scope.kind === "owner_sole" && (
          <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg bg-[var(--jc-bg)] border border-[var(--jc-border)]">
            <div className="min-w-0">
              <div className="font-semibold text-sm">Delete my company</div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-1">
                You're the only person in this workspace. Deleting the company
                also deletes your account and cancels billing at the end of the
                current period.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAction("delete-company")}
              data-testid="open-delete-company-btn"
              className="shrink-0 px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-warn,#b45309)] text-[var(--jc-warn,#b45309)] hover:bg-[var(--jc-warn,#b45309)]/10"
            >
              Delete my company
            </button>
          </div>
        )}
      </div>

      {/* ─────────── Modal: Delete my account ─────────── */}
      <Dialog
        open={action === "delete-account"}
        onOpenChange={(v) => !v && resetModal()}
      >
        <DialogContent data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle>Delete my account</DialogTitle>
            <DialogDescription>
              Your account will enter a 30-day grace period. You'll be signed
              out immediately. Restore anytime during the grace window from the
              sign-in screen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">
              Confirm with your password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="delete-account-password-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
              placeholder="Your password"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={resetModal}
              disabled={busy}
              data-testid="delete-account-cancel-btn"
              className="px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
            >
              Never mind
            </button>
            <button
              type="button"
              onClick={submitDeleteAccount}
              disabled={busy}
              data-testid="delete-account-confirm-btn"
              className="px-3 py-2 rounded-md text-xs font-semibold bg-[var(--jc-warn,#b45309)] text-white disabled:opacity-60 flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="animate-spin" size={13} /> : null}
              Delete my account
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────────── Modal: Transfer ownership ─────────── */}
      <Dialog
        open={action === "transfer"}
        onOpenChange={(v) => !v && resetModal()}
      >
        <DialogContent data-testid="transfer-ownership-modal">
          <DialogHeader>
            <DialogTitle>Share ownership</DialogTitle>
            <DialogDescription>
              Choose a teammate to promote to owner. They'll be able to manage
              billing, invitations, and settings. You'll remain an owner until
              you choose to delete your account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">
              Teammate to promote
            </label>
            <select
              value={transferTargetId}
              onChange={(e) => setTransferTargetId(e.target.value)}
              data-testid="transfer-target-select"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
            >
              <option value="">Choose a teammate…</option>
              {scope.eligibleTargets.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name || m.email || m.user_id}
                  {m.role === "owner" ? " (already owner)" : ""}
                </option>
              ))}
            </select>
            <label className="text-xs font-semibold text-[var(--jc-text-soft)] block mt-3">
              Confirm with your password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="transfer-password-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
              placeholder="Your password"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={resetModal}
              disabled={busy}
              data-testid="transfer-cancel-btn"
              className="px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
            >
              Never mind
            </button>
            <button
              type="button"
              onClick={submitTransfer}
              disabled={busy}
              data-testid="transfer-confirm-btn"
              className="px-3 py-2 rounded-md text-xs font-semibold jc-btn-primary disabled:opacity-60 flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="animate-spin" size={13} /> : null}
              Share ownership
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────────── Modal: Delete company ─────────── */}
      <Dialog
        open={action === "delete-company"}
        onOpenChange={(v) => !v && resetModal()}
      >
        <DialogContent data-testid="delete-company-modal">
          <DialogHeader>
            <DialogTitle>Delete company</DialogTitle>
            <DialogDescription>
              Your company will enter a 30-day grace period. Billing will
              cancel at the end of the current period. Every teammate is signed
              out immediately. You can restore the company anytime during the
              grace window.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">
              Type <span className="font-mono">DELETE COMPANY</span> to confirm
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              data-testid="delete-company-confirmation-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm font-mono"
              placeholder="DELETE COMPANY"
              autoCapitalize="characters"
            />
            <label className="text-xs font-semibold text-[var(--jc-text-soft)] block mt-3">
              Confirm with your password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="delete-company-password-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
              placeholder="Your password"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={resetModal}
              disabled={busy}
              data-testid="delete-company-cancel-btn"
              className="px-3 py-2 rounded-md text-xs font-semibold border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
            >
              Never mind
            </button>
            <button
              type="button"
              onClick={submitDeleteCompany}
              disabled={
                busy || confirmationText.trim() !== "DELETE COMPANY"
              }
              data-testid="delete-company-confirm-btn"
              className="px-3 py-2 rounded-md text-xs font-semibold bg-[var(--jc-warn,#b45309)] text-white disabled:opacity-60 flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="animate-spin" size={13} /> : null}
              Delete company
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountDangerZone;
